import React from 'react';
import { ButtonBase, makeStyles, createStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';

import InputProps from '../InputProps';
import BaseInputWrapper from '../BaseInputWrapper';
import { colors } from '../../../theme';
import { generateProperties, ButtonGroupElements } from '../helpers';

export interface ContainedButtonGroupProps extends InputProps<string> {
  buttons: ButtonGroupElements;
  disabledButtons?: string[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonContainer: {
      borderRadius: 10,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      width: '340px',
      border: `1px solid ${colors.gallery}`,
    },
    labelOffset: {
      marginTop: theme.spacing(3),
    },
    button: {
      // for some reason the global theme is being overriden by the user agent stylesheet. Including the fontFamily here for higher specificity
      fontFamily: 'Noto Sans SC',
      backgroundColor: 'transparent',
      color: colors.madison,
      padding: `${theme.spacing(2)}px ${theme.spacing(2)}px`,
      flexGrow: 1,
    },
    turquoiseButton: {
      // for some reason the global theme is being overriden by the user agent stylesheet. Including the fontFamily here for higher specificity
      backgroundColor: 'transparent',
      fontFamily: 'Noto Sans SC',
      fontWeight: 'bold',
      lineHeight: '14px',
      color: colors.light_silver,
      letterSpacing: '1.1px',
      padding: `${theme.spacing(2)}px ${theme.spacing(2)}px`,
      flexGrow: 1,
      textTransform: 'uppercase',
    },
    disabledButton: {
      color: colors.silver,
    },
    separator: {
      width: 1,
      backgroundColor: colors.gallery,
    },
    selectedButton: {
      backgroundColor: colors.madison,
      color: colors.white,
    },
    turquoiseSelectedButton: {
      backgroundColor: colors.white,
      borderRadius: 10,
      color: colors.bright_turquoise,
      border: `1px solid ${colors.bright_turquoise}`,
      fontWeight: 'bold',
    },
    disabledSelectedButton: {
      backgroundColor: colors.gallery,
      color: colors.silver,
    },
  }),
);

const ContainedButtonGroup = ({
  useLinealStyle,
  inputId,
  inputName,
  inputLabel,
  onChange,
  buttons,
  defaultValue = '',
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  disabledButtons = [],
  dataSource,
}: ContainedButtonGroupProps): JSX.Element => {
  const formInputProps = generateProperties<string>({
    useLinealStyle,
    inputName,
    onChange,
    defaultValue,
    inputError,
    inputErrorMessage,
    dataSource,
  });
  const classes = useStyles();

  function keyIsDisabled(key: string): boolean {
    return isDisabled || disabledButtons.includes(key);
  }

  function classNameForButton(disabled: boolean): string {
    return clsx(
      useLinealStyle ? classes.turquoiseButton : classes.button,
      disabled ? classes.disabledButton : '',
    );
  }

  function classNameForSelectedButton(disabled: boolean): string {
    return clsx(
      useLinealStyle ? classes.turquoiseButton : classes.button,
      useLinealStyle ? classes.turquoiseSelectedButton : classes.selectedButton,
      disabled ? classes.disabledSelectedButton : '',
    );
  }

  function makeButton(
    buttonKey: string,
    index: number,
    selected: boolean,
    id: string,
  ): React.ReactNode {
    const disabled = keyIsDisabled(buttonKey);
    return (
      <React.Fragment key={buttonKey}>
        {useLinealStyle
          ? null
          : index !== 0 && (
              <div
                data-testid="buttonSeparator"
                className={classes.separator}
              />
            )}
        <ButtonBase
          id={id}
          data-testid={buttonKey}
          className={
            selected
              ? classNameForSelectedButton(disabled)
              : classNameForButton(disabled)
          }
          onClick={(): void => formInputProps.onChange(buttonKey)}
          disabled={disabled}
        >
          {buttons[buttonKey]}
        </ButtonBase>
      </React.Fragment>
    );
  }

  return (
    <BaseInputWrapper
      inputId={inputId}
      label={inputLabel}
      error={formInputProps.error}
      errorMessage={formInputProps.errorText}
      disabled={isDisabled}
    >
      <div
        data-testid="container"
        className={clsx(
          classes.buttonContainer,
          typeof inputLabel !== 'undefined' && inputLabel !== ''
            ? classes.labelOffset
            : '',
        )}
      >
        {Object.keys(buttons).map(
          (buttonKey: string, index: number): React.ReactNode =>
            makeButton(
              buttonKey,
              index,
              buttonKey === formInputProps.value,
              `${inputId}-${index}`,
            ),
        )}
      </div>
    </BaseInputWrapper>
  );
};

export default ContainedButtonGroup;
